import { Dialog, DialogActions, DialogContent, Button } from "@mui/material";
import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  setRecomandModal,
  setSelectedAd,
} from "../../../redux/slices/LcaSlice";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import { Label } from "reactstrap";
import { toast } from "react-toastify";
import axiosClient from "../../../axios-client";
import { fetchMyservices } from "../../../redux/slices/bwvSlice";

const LcaRecomandModal = () => {
  const { selectedAd, recomandModal } = useSelector((state) => state.lcAds);
  const dispatch = useDispatch();
  const [service, setService] = useState([]);
  const [errors, setErrors] = useState(null);
  const [searchText, setSearchText] = useState(""); // State for search input
  const isFetching = useRef(false);
  const userinfo = useSelector((state) => state.user.userInfo);
  const { myServices2, currentPage } = useSelector((state) => state.bwvData);
  const islogin = useSelector((state) => state.user.isLogin);
  const user = islogin ? JSON.parse(userinfo) : null;

  const handleClose = () => {
    dispatch(setRecomandModal(false));
    setService([]);
    setErrors(null);
    setSearchText(""); // Clear search text on close
    dispatch(setSelectedAd(null));
  };

  const options = useMemo(
    () =>
      myServices2
        ?.map((list) => ({
          value: JSON.stringify(list),
          label: `${list.Title} ${list.firstName || ""} ${list.lastName || ""}`,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)) || [],
    [myServices2]
  );

  const fetchData = async () => {
    if (user && !isFetching.current) {
      isFetching.current = true; // Prevent multiple calls
      const serviceParams = {
        pageId: currentPage,
        pageSize: 20,
        postStatus: 3,
        searchText: searchText || undefined, // Pass searchText
      };
      Promise.all([dispatch(fetchMyservices(serviceParams))]).finally(() => {
        isFetching.current = false; // Reset fetching flag
      });
    }
  };

  useEffect(() => {
    fetchData();
  }, [recomandModal, searchText]); // Trigger fetchData when searchText changes

  // Enhanced custom styles for react-select with multi-select support
  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      borderColor: state.isFocused ? "#3f51b5" : "#ced4da",
      boxShadow: state.isFocused ? "0 0 0 1px #3f51b5" : "none",
      "&:hover": {
        borderColor: state.isFocused ? "#3f51b5" : "#aaa",
      },
      backgroundColor: "#fff",
      fontSize: "16px",
      padding: "2px",
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
      borderRadius: "4px",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
      backgroundColor: "#fff",
      maxHeight: "200px", // Limit dropdown height
      overflowY: "auto", // Enable scrolling for long lists
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "#3f51b5"
        : state.isFocused
          ? "#f5f5f5"
          : "#fff",
      color: state.isSelected ? "#fff" : "#333",
      padding: "10px 12px",
      fontSize: "16px",
      "&:hover": {
        backgroundColor: "#f5f5f5",
      },
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#999",
      fontSize: "16px",
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "#333",
      fontSize: "16px",
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: "#e8eaf6",
      borderRadius: "4px",
      fontSize: "14px",
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: "#3f51b5",
      padding: "2px 6px",
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: "#3f51b5",
      cursor: "pointer",
      "&:hover": {
        backgroundColor: "#c5cae9",
        color: "#fff",
      },
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
  };

  const handleSendRecommend = async () => {
    if (service.length === 0) {
      setErrors("Please select at least one service provider.");
      return;
    }

    // Extract unique createdBy IDs as numbers
    const userIds = [
      ...new Set(
        service
          .filter((s) => s.createdBy != null && !isNaN(Number(s.createdBy)))
          .map((s) => Number(s.createdBy))
      ),
    ];

    if (userIds.length === 0) {
      setErrors("No valid provider IDs selected.");
      return;
    }

    const request = {
      userIds,
      needId: selectedAd?.Id,
    };

    try {
      const response = await axiosClient.post(
        `message/recommendVendor`,
        request
      );
      if (response.data.statusCode === 200) {
        toast.success("Recommendation sent successfully");
        handleClose();
      } else {
        toast.error(response.data.msg || "Failed to send recommendation");
      }
    } catch (error) {
      toast.error("Error sending recommendation");
      console.error("API error:", error);
    }
  };

  return (
    <Dialog
      open={recomandModal}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      sx={{
        "& .MuiDialog-paper": {
          padding: "20px",
          minHeight: "200px",
          maxHeight: "80vh", // Limit dialog height to avoid overflow
          overflowY: "auto", // Keep dialog scrollable if content is long
        },
      }}
    >
      <DialogContent sx={{ overflow: "visible" }}> {/* Allow dropdown to overflow */}
        <div>
          <Label for="provider">Providers</Label>
          <Select
            options={options}
            onChange={(selectedOptions) =>
              setService(
                selectedOptions
                  ? selectedOptions.map((option) => JSON.parse(option.value))
                  : []
              )
            }
            onInputChange={(inputValue) => setSearchText(inputValue)} // Capture search input
            placeholder="Select Service Providers"
            isSearchable
            isMulti
            closeMenuOnSelect={false}
            styles={customStyles}
            className="react-select-container"
            classNamePrefix="react-select"
            menuPortalTarget={document.body} // Render dropdown in body
            menuPosition="fixed" // Use fixed positioning for better control
            noOptionsMessage={() =>
              errors || options.length === 0
                ? "No providers available"
                : "No matching providers"
            }
            value={options.filter((option) =>
              service.some((s) => JSON.stringify(s) === option.value)
            )}
          />
          {errors && (
            <p style={{ color: "red", marginTop: "10px" }}>{errors}</p>
          )}
        </div>
      </DialogContent>
      <DialogActions sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button onClick={handleClose} color="primary">
          Cancel
        </Button>
        <Button onClick={handleSendRecommend} color="primary" autoFocus>
          Recommend
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LcaRecomandModal;